/**
 * Shared Lumi audio cache.
 *
 * Lumi voice-moment/coaching lines are pre-written and approved. Audio is
 * generated ONCE by an admin via the seeding flow in
 * `/admin/instructor-voice` (which calls `lumi-voice` with `seed: true`)
 * and stored in the `lesson-narration` bucket. Static learner generation is
 * restricted to approved-lines.json. Explore summaries use a separate verified
 * `mode: "explore-summary"` path that sends a stored summaryId, not freeform
 * text.
 *
 * Client contract:
 *  - `preloadLumiTexts()` resolves a list of approved texts to signed URLs
 *    in the background when a lesson opens. Missing audio resolves to
 *    `null` and is logged (no toast, no overlay).
 *  - `getCachedLumiUrl()` lets the click handler skip the loading state
 *    when a URL is already resolved.
 *
 * Learners never submit arbitrary text — the manifests live in
 * `lumiBetaCoaching.ts` and `lumiLessonAudioManifest.ts`.
 */
import { supabase } from "@/integrations/supabase/client";

const urlCache = new Map<string, string>();
const inflight = new Map<string, Promise<LumiResolveResult>>();
const warmed = new Set<string>();

export interface LumiResolveResult {
  url: string | null;
  cached: boolean;
  generated: boolean;
  missing: boolean;
  reason?: string;
  textHashPrefix?: string;
}

export function getCachedLumiUrl(text: string): string | null {
  return urlCache.get(text.trim()) ?? null;
}

export function setCachedLumiUrl(text: string, url: string): void {
  urlCache.set(text.trim(), url);
}

function warmBrowserCache(url: string) {
  if (warmed.has(url) || typeof window === "undefined") return;
  warmed.add(url);
  try {
    const a = new Audio();
    a.preload = "auto";
    a.src = url;
    // Touch load() so the browser begins fetching into HTTP cache.
    a.load();
  } catch {
    /* ignore — preload is best-effort */
  }
}

export async function resolveLumiUrl(
  rawText: string,
  lessonId: string | null = null,
  options: { allowGenerate?: boolean; mode?: "static" | "explore-summary"; summaryId?: string | null } = {},
): Promise<LumiResolveResult> {
  const text = (rawText || "").trim();
  const mode = options.mode ?? "static";
  if (!text && mode !== "explore-summary") return { url: null, cached: false, generated: false, missing: true, reason: "empty_text" };
  const cacheKey = mode === "explore-summary" ? `explore-summary:${lessonId ?? "none"}:${options.summaryId ?? "none"}` : text;
  const cached = urlCache.get(cacheKey);
  if (cached) return { url: cached, cached: true, generated: false, missing: false };
  const existing = inflight.get(cacheKey);
  if (existing) return existing;

  const p = (async () => {
    try {
      const body = mode === "explore-summary"
        ? {
            mode: "explore-summary",
            lessonId,
            summaryId: options.summaryId,
            generate: options.allowGenerate === true,
          }
        : {
            text,
            lesson_id: lessonId,
            generate: options.allowGenerate === true,
          };
      const { data, error } = await supabase.functions.invoke("lumi-voice", {
        body,
      });
      const payload = data as (LumiResolveResult & { error?: string }) | null;
      const url = payload?.url ?? null;
      if (error || !url) {
        if (payload?.missing) {
          console.warn("Lumi audio asset unavailable — needs admin seeding", {
            lessonId,
            voiceId: "KR1TkIhkSykEjI4B0DtH",
            mode,
            reason: payload.reason,
          });
        } else {
          console.warn("Lumi audio lookup failed", { lessonId, error });
        }
        return {
          url: null,
          cached: false,
          generated: false,
          missing: payload?.missing ?? true,
          reason: payload?.reason ?? payload?.error ?? (error ? "function_error" : "missing_url"),
          textHashPrefix: payload?.textHashPrefix,
        };
      }
      urlCache.set(cacheKey, url);
      warmBrowserCache(url);
      return {
        url,
        cached: payload?.cached === true,
        generated: payload?.generated === true,
        missing: false,
        reason: payload?.reason,
        textHashPrefix: payload?.textHashPrefix,
      };
    } catch (e) {
      console.warn("Lumi audio resolve failed", e);
      return { url: null, cached: false, generated: false, missing: true, reason: "exception" };
    } finally {
      inflight.delete(cacheKey);
    }
  })();
  inflight.set(cacheKey, p);
  return p;
}

/**
 * Preload a batch of approved Lumi texts. Runs with limited concurrency to
 * avoid hammering the edge function on lesson load. Best-effort: missing
 * audio never blocks the UI.
 */
export interface PreloadOptions {
  allowGenerate?: boolean;
  mode?: "static" | "explore-summary";
  summaryId?: string | null;
}

export async function preloadLumiTexts(
  texts: Array<string | null | undefined>,
  lessonId: string | null = null,
  options: PreloadOptions = {},
): Promise<void> {
  const mode = options.mode ?? "static";
  const unique = Array.from(
    new Set(
      texts
        .map((t) => (t ?? "").trim())
        .filter((t) => t.length > 0),
    ),
  ).filter((t) => {
    const key = mode === "explore-summary"
      ? `explore-summary:${lessonId ?? "none"}:${options.summaryId ?? "none"}`
      : t;
    return !urlCache.has(key);
  });
  if (unique.length === 0) return;

  const CONCURRENCY = 2;
  let i = 0;
  const workers = Array.from({ length: Math.min(CONCURRENCY, unique.length) }, async () => {
    while (i < unique.length) {
      const text = unique[i++];
      await resolveLumiUrl(text, lessonId, {
        allowGenerate: options.allowGenerate === true,
        mode,
        summaryId: options.summaryId ?? null,
      }).catch(() => null);
    }
  });
  await Promise.all(workers);
}